import { useTranslation } from "react-i18next";
import TournamentCard from "./TournamentCard";
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Pagination from 'react-bootstrap/Pagination';
import { getLadderColor } from "../../util/HelperUtils";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import type { CSSProperties } from "react";
import type { Tournament } from "../../types/domain";

interface LadderFilter {
  key: string | null;
  ladderId: number | null;
  labelKey: string;
}

const LADDER_FILTERS: LadderFilter[] = [
  { key: null, ladderId: null, labelKey: 'all' },
  { key: 'beginners', ladderId: 3, labelKey: 'beginnerLadder' },
  { key: 'intermediate', ladderId: 2, labelKey: 'intermediateLadder' },
  { key: 'advanced', ladderId: 1, labelKey: 'advancedLadder' },
];

interface TournamentCardsProps {
  tournaments: Tournament[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  selectedLadder: string | null;
  onLadderChange: (ladderKey: string | null) => void;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

function TournamentCards({
  tournaments,
  currentPage,
  totalPages,
  onPageChange,
  selectedLadder,
  onLadderChange
}: TournamentCardsProps) {
  const { t } = useTranslation();

  const getFilterButtonStyle = (filter: LadderFilter, isActive: boolean): CSSProperties => {
    if (!isActive) return {};
    const color = filter.ladderId ? getLadderColor(filter.ladderId) : 'var(--text-primary)';
    return {
      backgroundColor: color,
      borderColor: color,
      color: '#000'
    };
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        onClick={() => onPageChange(0)}
        disabled={currentPage === 0}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 0}
      />
    );

    if (startPage > 0) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => onPageChange(i)}
        >
          {i + 1}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages - 1}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        onClick={() => onPageChange(totalPages - 1)}
        disabled={currentPage === totalPages - 1}
      />
    );

    return <Pagination className="tournament-cards-pagination">{items}</Pagination>;
  };

  return (
    <div className="tournament-cards-container">
      <div className="tournament-cards-filters">
        <ButtonGroup>
          {LADDER_FILTERS.map(filter => {
            const isActive = selectedLadder === filter.key;
            return (
              <Button
                key={filter.key || 'all'}
                variant={isActive ? 'primary' : 'outline-secondary'}
                onClick={() => onLadderChange(filter.key)}
                className="tournament-filter-btn"
                style={getFilterButtonStyle(filter, isActive)}
              >
                {filter.labelKey === 'all' ? t('all') : t(filter.labelKey)}
              </Button>
            );
          })}
        </ButtonGroup>
      </div>

      <motion.div
        className="tournament-cards-grid"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <AnimatePresence mode="popLayout">
          {tournaments.map((tournament: Tournament) => (
            <motion.div
              key={tournament.id}
              variants={itemVariants}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              layout
            >
              <TournamentCard tournament={tournament} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {tournaments.length === 0 && (
        <div className="tournament-cards-empty">
          {t("noTournamentsYet").replace("This player has", "There are")}
        </div>
      )}

      {renderPagination()}
    </div>
  );
}

export default TournamentCards;
